
import { useState, useEffect } from 'react';
import { mobileSupabase as supabase } from '@/integrations/supabase/mobile-client';
import { Child, Quote } from '@/types/child';
import { useToast } from './use-toast';
import { v4 as uuidv4 } from 'uuid';
import { logger } from '@/utils/logger';

export function useChildData(childId: string | undefined, userId: string | undefined) {
  const [child, setChild] = useState<Child | null>(null);
  const [editedChild, setEditedChild] = useState<Child | null>(null);
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const fetchChildData = async () => {
      if (!childId || !userId) {
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      
      try {
        logger.log(`Fetching child data for childId: ${childId}, userId: ${userId}`);
        
        // Simplified direct query for childId and userId
        const { data, error } = await supabase
          .from('children')
          .select('id, name, birthdate, photo_url')
          .eq('id', childId)
          .eq('user_id', userId)
          .single();
        
        if (error) {
          logger.error('Error fetching child:', error);
          toast({
            title: "Error",
            description: "Failed to load profile data. Please try again.",
            variant: "destructive"
          });
          setIsLoading(false);
          return;
        }
        
        if (!data) {
          logger.log('No child data found');
          setIsLoading(false);
          return;
        }
        
        const formattedChild: Child = {
          id: data.id,
          name: data.name,
          birthdate: data.birthdate,
          photoUrl: data.photo_url
        };
        
        logger.log('Formatted child data:', formattedChild);
        
        setChild(formattedChild);
        setEditedChild(formattedChild);
        
        // Fetch quotes
        const { data: quotesData, error: quotesError } = await supabase
          .from('quotes')
          .select('id, content, child_id, date, context')
          .eq('child_id', childId)
          .eq('user_id', userId)
          .order('date', { ascending: false });
        
        if (quotesError) {
          logger.error('Error fetching quotes:', quotesError);
        } else if (quotesData) {
          const formattedQuotes: Quote[] = quotesData.map(quote => ({
            id: quote.id,
            content: quote.content,
            childId: quote.child_id,
            childName: formattedChild.name,
            date: quote.date,
            context: quote.context || undefined
          }));
          
          logger.log('Formatted quotes data:', formattedQuotes);
          setQuotes(formattedQuotes);
        }
      } catch (error) {
        logger.error('Unexpected error in useChildData:', error);
        toast({
          title: "Error",
          description: "Failed to load profile data. Please try again.",
          variant: "destructive"
        });
      } finally {
        logger.log('Finished loading child data');
        setIsLoading(false);
      }
    };
    
    fetchChildData();
  }, [childId, userId, toast]);

  const updateChild = async () => {
    if (!editedChild || !userId) return false;
    
    logger.log('Updating child:', editedChild);
    
    try {
      const { error } = await supabase
        .from('children')
        .update({
          name: editedChild.name,
          birthdate: editedChild.birthdate
        })
        .eq('id', editedChild.id)
        .eq('user_id', userId);
      
      if (error) {
        logger.error('Error updating child:', error);
        toast({
          title: "Error",
          description: "Failed to update profile. Please try again.",
          variant: "destructive"
        });
        return false;
      }
      
      toast({
        title: "Success",
        description: "Profile updated successfully"
      });
      
      setChild(editedChild);
      return true;
    } catch (error) {
      logger.error('Unexpected error updating child:', error);
      toast({
        title: "Error",
        description: "Failed to update profile. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const updatePhoto = async (file: File) => {
    if (!child || !userId) return false;
    
    setIsUploading(true);
    
    try {
      // Generate a unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${uuidv4()}.${fileExt}`;
      const filePath = `${userId}/${child.id}/${fileName}`;
      
      // Upload the file to Supabase Storage
      const { error: uploadError } = await supabase.storage
        .from('child-photos')
        .upload(filePath, file);
      
      if (uploadError) {
        logger.error('Error uploading photo:', uploadError);
        toast({
          title: "Error",
          description: "Failed to upload photo. Please try again.",
          variant: "destructive"
        });
        setIsUploading(false);
        return false;
      }
      
      // Get the public URL
      const { data: publicUrlData } = supabase.storage
        .from('child-photos')
        .getPublicUrl(filePath);
      
      if (!publicUrlData || !publicUrlData.publicUrl) {
        throw new Error("Failed to get public URL for uploaded photo");
      }
      
      // Update the child record with the photo URL
      const { error: updateError } = await supabase
        .from('children')
        .update({
          photo_url: publicUrlData.publicUrl
        })
        .eq('id', child.id)
        .eq('user_id', userId);
      
      if (updateError) {
        logger.error('Error updating child with photo URL:', updateError);
        toast({
          title: "Error",
          description: "Failed to update profile with new photo. Please try again.",
          variant: "destructive"
        });
        setIsUploading(false);
        return false;
      }
      
      // Update the local state
      setChild({
        ...child,
        photoUrl: publicUrlData.publicUrl
      });
      
      setEditedChild({
        ...editedChild!,
        photoUrl: publicUrlData.publicUrl
      });
      
      toast({
        title: "Success",
        description: "Photo updated successfully"
      });
      
      setIsUploading(false);
      return true;
    } catch (error) {
      logger.error('Unexpected error updating photo:', error);
      toast({
        title: "Error",
        description: "Failed to update photo. Please try again.",
        variant: "destructive"
      });
      setIsUploading(false);
      return false;
    }
  };

  const deleteChild = async () => {
    if (!child || !userId) return false;
    
    try {
      const { error } = await supabase
        .from('children')
        .delete()
        .eq('id', child.id)
        .eq('user_id', userId);
      
      if (error) {
        logger.error('Error deleting child:', error);
        toast({
          title: "Error",
          description: "Failed to delete profile. Please try again.",
          variant: "destructive"
        });
        return false;
      }
      
      toast({
        title: "Success",
        description: "Profile deleted successfully"
      });
      
      return true;
    } catch (error) {
      logger.error('Unexpected error deleting child:', error);
      toast({
        title: "Error",
        description: "Failed to delete profile. Please try again.",
        variant: "destructive"
      });
      return false;
    }
  };

  const addQuote = (newQuote: any) => {
    if (child) {
      const formattedQuote: Quote = {
        ...newQuote,
        childName: child.name
      };
      setQuotes([formattedQuote, ...quotes]);
    }
  };

  return {
    child,
    editedChild,
    setEditedChild,
    quotes,
    isLoading,
    isUploading,
    updateChild,
    updatePhoto,
    deleteChild,
    addQuote
  };
}
